import { createContext, useContext, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import useIsWiderThan from "Utilities/Hooks/useIsWiderThan";

import { useGetDashboardCards } from "./API/queries";
import CategoryCard from "./CategoryCard/CategoryCard";
import ErrorBoundaryFallback from "./ErrorBoundaryFallback";

const CategoryCardContext = createContext();

/**
 * @returns {CategoryCardContext}
 */
const useCategoryCardContext = () => useContext(CategoryCardContext);

function DashboardCards() {
  const containerRef = useRef();

  const isMobile = !useIsWiderThan(containerRef, { px: 650 }) || false;

  const { data: { cards = [] } = {} } = useGetDashboardCards();

  return (
    <Wrapper $isMobile={isMobile} data-cy="DashboardCards_Container">
      <AnimatePresence>
        <Categories
          ref={containerRef}
          $isMobile={isMobile}
          $numCategories={cards.length}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: { type: "tween", delay: 0.75 },
          }}
          exit={{ opacity: 0 }}
        >
          {cards.map((category) => (
            <CategoryCardContext.Provider
              value={{
                isMobile,
                fullWidth: cards?.length === 1,
                ...category,
                // Override the customizations prop from the spread operator with the parsed customizations prop
                customizations: JSON.parse(category.customizations) || {},
              }}
              key={category.ID}
            >
              <CategoryCard />
            </CategoryCardContext.Provider>
          ))}
        </Categories>
      </AnimatePresence>
    </Wrapper>
  );
}

export { useCategoryCardContext };
export default function DashboardCardsWithErrorBoundary() {
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <DashboardCards />
    </ErrorBoundary>
  );
}
const Wrapper = styled.div`
  margin-left: 14px;
  margin-right: 6px;
`;

const Categories = styled(motion.div)`
  display: grid;
  grid-template-columns: ${({ $isMobile, $numCategories }) =>
    $isMobile ? "1fr" : `repeat(${$numCategories}, 1fr)`};
  gap: 10px;
`;
